import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canUseGiRegistry } from "../../helpers/geoEnabled";
import {
  getGiAuthorisedUser,
  saveGiAuthorisedUser,
  uploadGiLogo,
  uploadGiPdf,
} from "../../services/geoGiApi";
import { parseGeoPath, unwrapGeoPayload } from "./geoPath";
import "./gi-registry.scss";

const readAuthUser = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null") || {};
  } catch {
    return {};
  }
};

const GiAuthorisedUserForm = () => {
  const history = useHistory();
  const location = useLocation();
  const path = parseGeoPath(location.pathname, "gi-registry");
  const giId = path.id;
  const authUser: any = readAuthUser();
  const role = authUser?.users?.role;
  const isSuperAdmin = role === "SUPER_ADMIN";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [giName, setGiName] = useState("");
  const [applicationNumber, setApplicationNumber] = useState("");
  const [authorisedUserNumber, setAuthorisedUserNumber] = useState("");
  const [certificateUrl, setCertificateUrl] = useState("");
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    if (!canUseGiRegistry(role) || isSuperAdmin || !giId) {
      history.replace("/gi-registry");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getGiAuthorisedUser(giId)
      .then((res: any) => {
        if (cancelled) {
          return;
        }
        if (!res?.success) {
          setError(res?.message || "Unable to load authorised user details");
          return;
        }
        const data = unwrapGeoPayload(res) || {};
        setGiName(data.giName || data.name || "");
        setApplicationNumber(data.applicationNumber || "");
        setAuthorisedUserNumber(data.authorisedUserNumber || "");
        setCertificateUrl(data.certificateUrl || "");
        setLogoUrl(data.logoUrl || "");
      })
      .catch(() => {
        if (!cancelled) {
          setError("Unable to load authorised user details");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [giId, isSuperAdmin, role, history]);

  const handleCertificate = async (file?: File) => {
    if (!file) {
      return;
    }
    setSaving(true);
    try {
      const url = await uploadGiPdf(file);
      setCertificateUrl(url);
      notifications({
        title: "Success!",
        message: "Certificate uploaded",
        type: "success",
      });
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload the certificate",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleLogo = async (file?: File) => {
    if (!file) {
      return;
    }
    setSaving(true);
    try {
      const url = await uploadGiLogo(file);
      setLogoUrl(url);
      notifications({
        title: "Success!",
        message: "Logo uploaded",
        type: "success",
      });
    } catch (err: any) {
      notifications({
        title: "Error!",
        message: err?.message || "Could not upload the logo",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!giId) {
      return;
    }
    const number = authorisedUserNumber.trim();
    if (!number) {
      notifications({
        title: "Error!",
        message: "Authorised user number is required",
        type: "danger",
      });
      return;
    }
    if (!certificateUrl) {
      notifications({
        title: "Error!",
        message: "Upload the authorised user certificate PDF",
        type: "danger",
      });
      return;
    }
    if (!logoUrl) {
      notifications({
        title: "Error!",
        message: "Upload the GI authorised user logo",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const res: any = await saveGiAuthorisedUser(giId, {
        authorisedUserNumber: number,
        certificateUrl,
        logoUrl,
      });
      if (!res?.success) {
        notifications({
          title: "Error!",
          message: res?.message || "Could not save authorised user details",
          type: "danger",
        });
        return;
      }
      notifications({
        title: "Success!",
        message: "Authorised user details saved",
        type: "success",
      });
      history.push("/gi-registry");
    } catch {
      notifications({
        title: "Error!",
        message: "Could not save authorised user details",
        type: "danger",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="gi-registry-page">Loading authorised user details…</div>;
  }

  if (error) {
    return (
      <div className="gi-registry-page">
        <div className="gi-registry-toolbar">
          <h2>GI authorised user</h2>
          <Button className="ResetBtn" onClick={() => history.push("/gi-registry")}>
            Back to list
          </Button>
        </div>
        <div className="gi-form-banner">{error}</div>
      </div>
    );
  }

  return (
    <div className="gi-registry-page">
      <div className="gi-registry-toolbar">
        <h2>GI authorised user</h2>
        <div className="gi-form-actions" style={{ marginTop: 0 }}>
          <Button className="btn-hastkala-outline" onClick={() => history.push("/gi-registry")}>
            {authorisedUserNumber.trim() || certificateUrl || logoUrl
              ? "Back to list"
              : "Continue without authorised user details"}
          </Button>
          {giId ? (
            <Button
              className="btn-hastkala-outline"
              onClick={() => history.push(`/gi-registry/${giId}`)}
            >
              View GI
            </Button>
          ) : null}
          <Button className="ResetBtn" disabled={saving} onClick={handleSave}>
            {saving ? "Saving…" : "Save authorised user"}
          </Button>
        </div>
      </div>
      <p className="gi-au-hint">
        {giName || "This GI"}
        {applicationNumber ? ` · App no. ${applicationNumber}` : ""} is already in your studio.
        Authorised user number, certificate, and logo are optional. Continue without them and add
        them later, or save them now. This is not the GI’s own registration certificate.
      </p>
      <div className="gi-form-fields gi-au-form">
        <Label htmlFor="auNumber">Authorised user number</Label>
        <Input
          id="auNumber"
          value={authorisedUserNumber}
          onChange={(e) => setAuthorisedUserNumber(e.target.value)}
          placeholder="Number on the authorised user certificate"
          maxLength={64}
        />
        <Label htmlFor="auCertificate">Certificate of GI authorised user (PDF)</Label>
        <Input
          id="auCertificate"
          type="file"
          accept="application/pdf,.pdf"
          disabled={saving}
          onChange={(e) => {
            handleCertificate(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {certificateUrl ? (
          <p className="mb-2">
            <a href={certificateUrl} target="_blank" rel="noreferrer">
              Open certificate
            </a>
            {" · "}
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => setCertificateUrl("")}
            >
              Remove
            </button>
          </p>
        ) : (
          <p className="gi-au-hint">PDF, 10 MB or smaller.</p>
        )}
        <Label htmlFor="auLogo">GI authorised user logo</Label>
        <Input
          id="auLogo"
          type="file"
          accept="image/jpeg,image/png,.jpg,.jpeg,.png"
          disabled={saving}
          onChange={(e) => {
            handleLogo(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {logoUrl ? (
          <>
            <img className="gi-au-logo-preview" src={logoUrl} alt="GI authorised user logo" />
            <p className="mb-2">
              <button type="button" className="btn btn-link p-0" onClick={() => setLogoUrl("")}>
                Remove
              </button>
            </p>
          </>
        ) : (
          <p className="gi-au-hint">JPEG or PNG, 2 MB or smaller.</p>
        )}
      </div>
    </div>
  );
};

export default GiAuthorisedUserForm;
